/**
 * Abstract report template visitor.
 *
 * Implement visiting methods for all report template item classes.
 * All visiting method return the result of the visit of the object.
 *
 * The additional API method `visitAll` maps over a collection, visiting
 * each element.
 *
 * @abstract
 */
export default class ReportTemplateVisitor extends Object {
	visit(object) {
		return object.accept(this);
	}

	/**
	 * The default implementation returns a {columns, rows} object holding
	 * the result of the visit of each row and map separately.
	 */
	visitReportTemplate(reportTemplate) {
		return {
			columns: this.visitAll(reportTemplate.getColumns()),
			rows: this.visitAll(reportTemplate.getRows()),
		};
	}

	/**
	 * Map over `collection`, visiting each element.
	 */
	visitAll(collection) {
		return collection.map((item) => this.visit(item));
	}

	visitAbstractItemTemplate() {}

	visitAbstractColumnTemplate(column) {
		return this.visitAbstractItemTemplate(column);
	}

	visitAbstractPeriodColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitOpeningBalanceColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitClosingBalanceColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitPeriodChangeColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitPredictedForecastColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitCurrentPeriodForecastColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitReferenceForecastColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitYearToDateColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitRollingPeriodColumnTemplate(column) {
		return this.visitAbstractPeriodColumnTemplate(column);
	}

	visitRepeatingColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitAbstractGroupColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitManualGroupColumnTemplate(column) {
		return this.visitAbstractGroupColumnTemplate(column);
	}

	visitDeviationColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitFormulaColumnTemplate(row) {
		return this.visitAbstractColumnTemplate(row);
	}

	visitPercentageColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitSeparatorColumnTemplate(column) {
		return this.visitAbstractColumnTemplate(column);
	}

	visitAbstractRowTemplate(row) {
		return this.visitAbstractItemTemplate(row);
	}

	visitAbstractValueRowTemplate(row) {
		return this.visitAbstractRowTemplate(row);
	}

	visitAbstractAccountRowTemplate(row) {
		return this.visitAbstractValueRowTemplate(row);
	}

	visitAccountGroupRowTemplate(row) {
		return this.visitAbstractAccountRowTemplate(row);
	}

	visitGeneratedAccountGroupRowTemplate(row) {
		return this.visitAccountGroupRowTemplate(row);
	}

	visitAccountIntervalGroupRowTemplate(row) {
		return this.visitAbstractAccountRowTemplate(row);
	}

	visitAccountRowTemplate(row) {
		return this.visitAbstractAccountRowTemplate(row);
	}

	visitGeneratedAccountRowTemplate(row) {
		return this.visitAccountRowTemplate(row);
	}

	visitSpacerRowTemplate(row) {
		return this.visitAbstractRowTemplate(row);
	}

	visitTextRowTemplate(row) {
		return this.visitAbstractRowTemplate(row);
	}

	visitGroupRowTemplate(group) {
		return this.visitAbstractAccountRowTemplate(group);
	}

	visitRepeatingRowTemplate(group) {
		return this.visitGroupRowTemplate(group);
	}

	visitFormulaRowTemplate(row) {
		return this.visitAbstractValueRowTemplate(row);
	}

	visitKeyFigureRowTemplate(row) {
		return this.visitAbstractValueRowTemplate(row);
	}
}
